import * as z from 'zod';

// ---------------------------------------------------------------------------
// Per-game feature configuration - what the manager handles vs. what the table
// handles. Chosen as a preset at setup (see GAME_FEATURE_PRESETS) and editable
// at any time from the in-game Configurations tab via SET_GAME_FEATURES.
// ---------------------------------------------------------------------------

const HordeGameFeaturesFieldsSchema = z.object({
    autoSpawnRolls: z.boolean(),
    autoSpawnSelection: z.boolean(),
    battleShockPrompts: z.boolean(),
    commandPoints: z.boolean(),
    hordePhaseWalkthrough: z.boolean(),
    resupplyPurchases: z.boolean(),
    spawnBracketTab: z.boolean(),
    spawnWalkthrough: z.boolean(),
    supplyPoints: z.boolean(),
});

// The two auto-spawn flags default to `false` (unlike every other key): a stored features object missing them
// predates the flags, and silently auto-rolling dice in an existing manual game would be surprising. New games get
// them stamped explicitly by their preset instead (Lite = auto, Full = manual - TODO.md #5).
export const HordeGameFeaturesSchema = HordeGameFeaturesFieldsSchema.extend({
    autoSpawnRolls: HordeGameFeaturesFieldsSchema.shape.autoSpawnRolls.default(false),
    autoSpawnSelection: HordeGameFeaturesFieldsSchema.shape.autoSpawnSelection.default(false),
    battleShockPrompts: HordeGameFeaturesFieldsSchema.shape.battleShockPrompts.default(true),
    commandPoints: HordeGameFeaturesFieldsSchema.shape.commandPoints.default(true),
    hordePhaseWalkthrough: HordeGameFeaturesFieldsSchema.shape.hordePhaseWalkthrough.default(true),
    resupplyPurchases: HordeGameFeaturesFieldsSchema.shape.resupplyPurchases.default(true),
    spawnBracketTab: HordeGameFeaturesFieldsSchema.shape.spawnBracketTab.default(true),
    spawnWalkthrough: HordeGameFeaturesFieldsSchema.shape.spawnWalkthrough.default(true),
    supplyPoints: HordeGameFeaturesFieldsSchema.shape.supplyPoints.default(true),
});
export type HordeGameFeatures = z.infer<typeof HordeGameFeaturesSchema>;

/**
 * Sparse patch shape used by the SET_GAME_FEATURES intent. Built from the undefaulted field schemas
 * (rather than `HordeGameFeaturesSchema.partial()`) so an omitted key stays absent after parsing instead of being
 * backfilled with that field's `.default(true)` - the SET_GAME_FEATURES reducer and its log narration both
 * rely on the parsed patch containing only the keys the caller actually set.
 */
export const HordeGameFeaturesPatchSchema = HordeGameFeaturesFieldsSchema.partial();
export type HordeGameFeaturesPatch = z.infer<typeof HordeGameFeaturesPatchSchema>;

export const FULL_GAME_FEATURES: HordeGameFeatures = {
    autoSpawnRolls: false,
    autoSpawnSelection: false,
    battleShockPrompts: true,
    commandPoints: true,
    hordePhaseWalkthrough: true,
    resupplyPurchases: true,
    spawnBracketTab: true,
    spawnWalkthrough: true,
    supplyPoints: true,
};

export const LITE_GAME_FEATURES: HordeGameFeatures = {
    autoSpawnRolls: true,
    autoSpawnSelection: true,
    battleShockPrompts: false,
    commandPoints: false,
    hordePhaseWalkthrough: false,
    resupplyPurchases: false,
    spawnBracketTab: true,
    spawnWalkthrough: true,
    supplyPoints: false,
};

export const HordeGameManagerModeSchema = z.enum(['full-walkthrough', 'spawn-and-cards']);
export type HordeGameManagerMode = z.infer<typeof HordeGameManagerModeSchema>;

/** The feature set each setup manager-mode choice stamps onto a new game. */
export const GAME_FEATURE_PRESETS: Record<HordeGameManagerMode, HordeGameFeatures> = {
    'full-walkthrough': FULL_GAME_FEATURES,
    'spawn-and-cards': LITE_GAME_FEATURES,
};

/** User-visible label for each feature key - shared by the Configurations tab and the command log. */
export const GAME_FEATURE_LABELS: Record<keyof HordeGameFeatures, string> = {
    autoSpawnRolls: 'Auto Spawn Rolls',
    autoSpawnSelection: 'Auto Spawn selection',
    battleShockPrompts: 'Battle-shock tests',
    commandPoints: 'Track Command Points',
    hordePhaseWalkthrough: 'Step-by-step Horde phases',
    resupplyPurchases: 'Resupply Table purchases',
    spawnBracketTab: 'Spawn Bracket tab',
    spawnWalkthrough: 'Guided Spawn Roll',
    supplyPoints: 'Track Supply Points',
};

/** One-line explanation of what each feature turns on, shown under its switch. */
export const GAME_FEATURE_DESCRIPTIONS: Record<keyof HordeGameFeatures, string> = {
    autoSpawnRolls:
        'Roll each Spawn Roll for you automatically. A Re-roll button stays available, and you can still type a physical roll instead.',
    autoSpawnSelection:
        'Pick a valid Horde unit from the rolled bracket for you automatically. You can still pick another at random or choose two from the bracket below.',
    battleShockPrompts:
        "Prompt a Battle-shock test for each Horde unit at or below half strength, and for the players' Battle-shock step.",
    commandPoints: 'Show each player’s Command Points in the app. Off means the table tracks them itself.',
    hordePhaseWalkthrough:
        'Walk Movement, Shooting, Charge, Fight and the players’ Command phase one unit at a time. Off shows the round as a high-level checklist instead.',
    resupplyPurchases: 'Buy from the Resupply Table in the app. Off shows the same table as reference only.',
    spawnBracketTab: 'Add a Spawn Bracket tab for looking up what can spawn in each bracket.',
    spawnWalkthrough:
        'Walk each Spawn Zone through clear, roll, choose, roll-down and place. Off leaves spawning to the table.',
    supplyPoints:
        'Award and total each player’s Supply Points in the app. Off means the table tracks them on paper.',
};

/** Ordered key list for rendering the Configurations tab. */
export const GAME_FEATURE_KEYS = [
    'hordePhaseWalkthrough',
    'battleShockPrompts',
    'spawnWalkthrough',
    'autoSpawnRolls',
    'autoSpawnSelection',
    'spawnBracketTab',
    'supplyPoints',
    'commandPoints',
    'resupplyPurchases',
] as const satisfies readonly (keyof HordeGameFeatures)[];

/**
 * Effective feature flags for a game: the explicitly stored set when present, otherwise the Lite preset
 * (legacy saves and never-configured games). Mirrors the old `GameStore`'s own null-game default.
 */
export function resolveGameFeatures(game: { features?: HordeGameFeatures } | null | undefined): HordeGameFeatures {
    return game?.features ?? LITE_GAME_FEATURES;
}

/** True when the supplied features exactly match a named preset (used for the shell’s mode badge). */
export function matchesGameFeaturePreset({
    features,
    preset,
}: {
    features: HordeGameFeatures;
    preset: HordeGameFeatures;
}): boolean {
    return (Object.keys(preset) as (keyof HordeGameFeatures)[]).every((key) => features[key] === preset[key]);
}
